import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import useInstructorDashboard from './useInstructorDashboard';
import useInstructorActiveAttendanceSessions from './useInstructorActiveAttendanceSessions';
import SessionQnaSplit from '../sessionQna/SessionQnaSplit';
import { extractWeekNumber } from './activeAttendanceSessionsHelpers';
import { ApproveModal, DenyModal } from '../../pages/admin/extensions/adminExtensions/ExtensionModals';
import apiService from '../../services/api';
import LiveBanner from './instructorHome/LiveBanner';
import Lanes from './instructorHome/Lanes';
import QuickAdd from './instructorHome/QuickAdd';
import CourseCards from './instructorHome/CourseCards';
import useUpcomingCancellations from '../courses/useUpcomingCancellations';
import useInstructorLanes from './instructorHome/useInstructorLanes';
import useQuickAdd from './instructorHome/useQuickAdd';
import { pickDefaultCourse, ZONE } from './instructorHome/instructorHome';
import styles from './instructorHome/InstructorHome.module.css';

const qnaTitle = (s) => {
  if (!s) return '';
  const week = extractWeekNumber(s.title);
  const code = s.course_code || s.elective_code || '';
  return [code, week ? `Week ${week}` : (code ? '' : s.title || '')].filter(Boolean).join(' · ');
};

/** Instructor dashboard — D2 (three lanes) with Q2 (Quick Add). */
function InstructorDashboard() {
  const {
    user, navigate, loading, taughtCourses, currentCourses, filteredCourses,
    showInactiveCourses, setShowInactiveCourses,
  } = useInstructorDashboard();
  const { initialLoadDone, getVisibleSessions } = useInstructorActiveAttendanceSessions();
  const todaySessions = getVisibleSessions();
  const liveSessions = useMemo(() => todaySessions.filter((s) => s.isActive), [todaySessions]);
  const bannerSession = liveSessions[0] || todaySessions[0] || null;

  const activeCourseIds = useMemo(() => currentCourses.map((c) => c.id), [currentCourses]);
  // Classes canceled in the next few weeks, so the course cards do not call a canceled class the next one.
  const cancellations = useUpcomingCancellations(true);
  const { lanes, loaded: lanesLoaded, reloadWork, reloadQna } = useInstructorLanes({
    enabled: !!user && !loading, liveSessions, activeCourseIds,
  });

  const [qnaSession, setQnaSession] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [denyTarget, setDenyTarget] = useState(null);

  const liveCourseId = liveSessions.find((s) => s.course_id)?.course_id || null;
  const defaultCourse = useMemo(() => pickDefaultCourse(currentCourses, liveCourseId), [currentCourses, liveCourseId]);
  const quick = useQuickAdd({ courses: currentCourses, defaultCourseId: defaultCourse?.id || null });

  const toGradeByCourse = useMemo(() => {
    const m = new Map();
    lanes.grading.forEach((r) => { if (r.courseId) m.set(r.courseId, (m.get(r.courseId) || 0) + r.count); });
    return m;
  }, [lanes.grading]);

  // Reveal everything below the header in one step instead of sections popping
  // in one by one. The timeout keeps a slow request from hiding it forever.
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 4000);
    return () => clearTimeout(t);
  }, []);
  const ready = timedOut || (!loading && initialLoadDone && lanesLoaded);

  const openManage = useCallback((course, tab) => {
    if (!course) return;
    navigate(`/courses/${course.id}?manageContent=true${tab ? `&tab=${tab}` : ''}`);
  }, [navigate]);

  const closeQna = useCallback(() => { setQnaSession(null); reloadQna(); }, [reloadQna]);

  const today = DateTime.now().setZone(ZONE).toFormat('cccc, LLLL d');

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>{today}</span>
        <h1 className={styles.greeting}>
          <span dir="ltr">{user?.first_name || 'Instructor'}</span>{' '}
          <span dir="rtl">السلام عليكم</span>
        </h1>
        <p className={styles.subtitle}>Here’s what’s happening today.</p>
      </header>

      <div style={{ display: ready ? 'contents' : 'none' }}>
        <LiveBanner
          session={bannerSession}
          onOpenQna={setQnaSession}
          onAttendance={() => navigate('/admin/attendance')}
        />

        {qnaSession && (
          <div>
            <SessionQnaSplit
              open
              onClose={closeQna}
              sessionId={qnaSession.session_id}
              subtitle={qnaTitle(qnaSession)}
            />
          </div>
        )}

        <Lanes
          lanes={lanes}
          courseCount={filteredCourses.length}
          coursesCard={!loading && (
            <CourseCards
              courses={filteredCourses}
              allCount={taughtCourses.length}
              showPrevious={showInactiveCourses}
              onTogglePrevious={() => setShowInactiveCourses(!showInactiveCourses)}
              toGradeByCourse={toGradeByCourse}
              cancellations={cancellations}
              onAddContent={(c) => openManage(c)}
              onOpen={(c) => navigate(`/courses/${c.id}`)}
              onStudents={(c) => navigate(`/admin/courses/${c.id}/students`)}
              onGradebook={(c) => navigate(`/instructor/gradebook?courseId=${c.id}&tab=pending-grading`)}
              onAttendance={(c) => navigate(`/admin/attendance?courseId=${c.id}`)}
            />
          )}
          onApprove={setApproveTarget}
          onDecline={setDenyTarget}
          onGrade={(row) => navigate(row.href)}
          onAllRequests={() => navigate('/admin/extensions')}
          onGradingInbox={() => navigate(lanes.grading[0]?.kind === 'quiz' ? '/instructor/quizzes' : '/assignments')}
        />

        <QuickAdd
          quick={quick}
          courses={currentCourses}
          onNewAssignment={() => navigate('/assignments?new=1')}
          onNewQuiz={() => navigate('/instructor/quizzes/new')}
          onAllAssignments={() => navigate('/assignments')}
          onAllQuizzes={() => navigate('/instructor/quizzes')}
          onManage={openManage}
        />
      </div>

      <ApproveModal
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        request={approveTarget}
        onConfirm={async ({ durationHours, note }) => {
          await apiService.approveExtension(approveTarget.id, { durationHours, note });
          setApproveTarget(null);
          reloadWork();
        }}
      />
      <DenyModal
        open={!!denyTarget}
        onClose={() => setDenyTarget(null)}
        request={denyTarget}
        onConfirm={async ({ note }) => {
          await apiService.denyExtension(denyTarget.id, { note });
          setDenyTarget(null);
          reloadWork();
        }}
      />
    </div>
  );
}

export default InstructorDashboard;
